export interface PasswordIdentityContext {
  email?: string | null;
  username?: string | null;
  employeeCode?: string | number | null;
  fullName?: string | null;
}

export interface PasswordPolicyResult {
  isValid: boolean;
  score: number;
  label: 'Empty' | 'Weak' | 'Fair' | 'Good' | 'Strong';
  percent: number;
  colorClassName: string;
  firstError?: string;
  checks: {
    length: boolean;
    printable: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
    uncommon: boolean;
    notSimilar: boolean;
  };
}

const SPECIAL_CHARACTER_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
const PRINTABLE_ASCII_REGEX = /^[\x20-\x7E]+$/;
const COMMON_PASSWORD_WORDS = [
  'password',
  'admin',
  'welcome',
  'qwerty',
  'test',
  'default',
  'customer',
  'connect',
  'sapphire',
  'company',
  'user',
  'login',
  'portal',
];

function normalizeToken(value?: string | number | null): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function getIdentityTokens(context: PasswordIdentityContext = {}): string[] {
  const email = String(context.email || '').toLowerCase();
  const emailPrefix = email.includes('@') ? email.split('@')[0] : email;
  const fullNameTokens = String(context.fullName || '')
    .split(/\s+/)
    .map(normalizeToken);

  return [
    normalizeToken(email),
    normalizeToken(emailPrefix),
    normalizeToken(context.username),
    normalizeToken(context.employeeCode),
    normalizeToken(context.fullName),
    ...fullNameTokens,
  ].filter(token => token.length >= 4);
}

function hasCommonWord(password: string): boolean {
  const normalizedPassword = normalizeToken(password);
  return COMMON_PASSWORD_WORDS.some(word => normalizedPassword.includes(word));
}

function isSimilarToIdentity(
  password: string,
  context: PasswordIdentityContext = {},
): boolean {
  const normalizedPassword = normalizeToken(password);
  if (!normalizedPassword) return false;

  return getIdentityTokens(context).some(token => {
    return normalizedPassword.includes(token) || token.includes(normalizedPassword);
  });
}

export function getPasswordPolicyResult(
  password: string,
  context: PasswordIdentityContext = {},
): PasswordPolicyResult {
  const value = String(password || '');
  const checks = {
    length: value.length >= 8 && value.length <= 32,
    printable: !value || PRINTABLE_ASCII_REGEX.test(value),
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /\d/.test(value),
    special: SPECIAL_CHARACTER_REGEX.test(value),
    uncommon: !hasCommonWord(value),
    notSimilar: !isSimilarToIdentity(value, context),
  };

  const errors: Array<[keyof typeof checks, string]> = [
    ['length', 'Password must be 8 to 32 characters'],
    ['printable', 'Use printable ASCII characters only'],
    ['uppercase', 'Must contain an uppercase letter'],
    ['lowercase', 'Must contain a lowercase letter'],
    ['number', 'Must contain a number'],
    ['special', 'Must contain a special character'],
    ['uncommon', 'Password must not contain common words'],
    ['notSimilar', 'Password must not be similar to user details'],
  ];

  const score = Object.values(checks).filter(Boolean).length;
  const percent = value ? Math.round((score / Object.keys(checks).length) * 100) : 0;
  const label: PasswordPolicyResult['label'] =
    !value ? 'Empty' :
    score <= 4 ? 'Weak' :
    score <= 6 ? 'Fair' :
    score === 7 ? 'Good' :
    'Strong';
  const colorClassName =
    label === 'Strong' ? 'bg-emerald-500' :
    label === 'Good' ? 'bg-blue-500' :
    label === 'Fair' ? 'bg-amber-500' :
    'bg-red-500';

  return {
    isValid: score === Object.keys(checks).length,
    score,
    label,
    percent,
    colorClassName,
    firstError: errors.find(([key]) => !checks[key])?.[1],
    checks,
  };
}

export function validatePasswordPolicy(
  password: string,
  context: PasswordIdentityContext = {},
): true | string {
  const result = getPasswordPolicyResult(password, context);
  return result.isValid || result.firstError || 'Password does not meet policy';
}
